import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MessageCircle, Search, ArrowLeft, CheckCheck, 
  Clock, User, Calendar, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { api, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { Input } from '../components/ui/input';

// Format time like WhatsApp
const formatLastMessageTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) {
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  } else if (days === 1) {
    return 'Yesterday';
  } else if (days < 7) {
    return date.toLocaleDateString('en-IN', { weekday: 'short' });
  } else {
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit' });
  }
};

const ConversationsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchConversations();
    // Poll for updates
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchConversations = async () => {
    try {
      const res = await api.get('/conversations');
      setConversations(res.data || []);
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const getUnreadCount = (conv) => {
    if (user.role === 'client') return conv.unread_client || 0;
    return conv.unread_lawyer || 0;
  };

  const getOtherPartyName = (conv) => {
    return user.role === 'client' ? conv.lawyer_name : conv.client_name;
  };

  const getOtherPartyInitial = (conv) => {
    const name = getOtherPartyName(conv);
    return name?.charAt(0)?.toUpperCase() || '?';
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const name = getOtherPartyName(conv);
    return name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="conversations-page">
      <Navbar />
      
      {/* Header */}
      <div className="bg-[#075e54] text-white">
        <div className="container-app py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Messages</h1>
              <p className="text-emerald-200 text-sm mt-1">
                {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
              </p>
            </div>
            <MessageCircle className="w-10 h-10 text-emerald-200" />
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="container-app py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-conversations"
            />
          </div>
        </div>
      </div>

      {/* Conversations List */}
      <div className="container-app py-4">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-slate-200 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 bg-slate-200 rounded w-1/3 mb-2" />
                    <div className="h-3 bg-slate-200 rounded w-2/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="text-center py-16">
            <MessageCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900">No conversations yet</h3>
            <p className="text-slate-500 mt-2">
              {user.role === 'client' 
                ? 'Book a consultation to start chatting with a lawyer'
                : 'Accept a booking to start chatting with clients'
              }
            </p>
            {user.role === 'client' && (
              <Link
                to="/lawyers"
                className="inline-block mt-4 px-6 py-2 bg-[#075e54] text-white rounded-full hover:bg-[#054c44] transition-colors"
              >
                Find Lawyers
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredConversations.map((conv, idx) => {
              const unread = getUnreadCount(conv);
              return (
                <motion.div
                  key={conv.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <Link
                    to={`/chat/${conv.id}`}
                    className="block bg-white rounded-xl hover:bg-slate-50 transition-colors border border-slate-200 overflow-hidden"
                    data-testid={`conversation-${idx}`}
                  >
                    <div className="p-4 flex items-center gap-4">
                      {/* Avatar */}
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold shrink-0 ${
                        unread > 0 
                          ? 'bg-[#075e54] text-white' 
                          : 'bg-slate-200 text-slate-600'
                      }`}>
                        {getOtherPartyInitial(conv)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className={`font-semibold truncate ${unread > 0 ? 'text-slate-900' : 'text-slate-700'}`}>
                            {getOtherPartyName(conv)}
                          </h3>
                          <span className={`text-xs whitespace-nowrap ${unread > 0 ? 'text-[#075e54] font-semibold' : 'text-slate-400'}`}>
                            {formatLastMessageTime(conv.last_message_time)}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between gap-2 mt-1">
                          <p className={`text-sm truncate ${unread > 0 ? 'text-slate-700' : 'text-slate-500'}`}>
                            {conv.last_message || 'No messages yet'}
                          </p>
                          {unread > 0 && (
                            <span className="bg-[#075e54] text-white text-xs px-2 py-0.5 rounded-full min-w-[20px] text-center">
                              {unread}
                            </span>
                          )}
                        </div>

                        {/* Booking info */}
                        {conv.booking && (
                          <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                            <Calendar className="w-3 h-3" />
                            <span>{conv.booking.date}</span>
                            <span className={`px-1.5 py-0.5 rounded ${
                              conv.booking.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                              conv.booking.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              conv.booking.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {conv.booking.status}
                            </span>
                          </div>
                        )}
                      </div>

                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationsPage;
